/* global JSZip */
/* n.b. If you try to import JSZip as a node package, dragonfly workers will explode. You have been warned. */

// @ts-check
import GLU from '../glu2.js/src/index';
import Api from '../apis/Api';
import AppConfig from '../appConfig';
import { dbGet, dbSet } from '../helpers/IndexedDB';
import { retry } from '../helpers/retry';

const POINTS_LOAD_RETRY = { attempts: 3, delayMs: 1000 };

class PointsDataSource extends GLU.DataSource {
    static get name() {
        return 'PointsDataSource';
    }

    static getInstance() {
        return new PointsDataSource();
    }

    constructor() {
        super();
        /** @type {import('../../../').GeoJson} */
        this._pointsGeoJson = undefined;
        /** @type {import('../../../').PointsMetadata} */
        this._pointsMetadata = undefined;
        /** @type {Promise<void>} */
        this._pointsMetadataLoad = undefined;
        this._serviceCodes = undefined;
    }

    /** @returns {import('../../../').GeoJson} */
    get pointsGeoJson() {
        return this._pointsGeoJson;
    }

    /**
     * @returns {import('../../../').PointsMetadata}
     */
    get pointsMetadata() {
        return this._pointsMetadata;
    }

    get serviceCodes() {
        return this._serviceCodes;
    }

    get pointsSourceId() {
        return this._pointsMetadata.mapProperties.sourceId;
    }

    get pointsLayerId() {
        return this._pointsMetadata.mapProperties.layerId;
    }

    get pointsClusteredLayerId() {
        return this._pointsMetadata.mapProperties.clusteredLayerId;
    }

    loadPointsGeoJson = () =>
        retry(this.fetchPointsGeoJson, POINTS_LOAD_RETRY).then(
            geoJson => {
                this._pointsGeoJson = {
                    type: 'FeatureCollection',
                    features: geoJson.features.filter(f => !!f.geometry),
                };
            },
            e => {
                console.error(e);
                AppConfig.sentry.captureException(e);
            },
        );

    /** @returns {Promise<import('../../../').GeoJson>} */
    fetchPointsGeoJson = async () => {
        const { collectionName, collectionKey } = this.pointsMetadata.data.db;
        const { archive, raw } = this.pointsMetadata.data.file;
        const eTagCollectionKeyName = `${collectionKey}-etag`;

        const signedUrlResponse = await fetch(
            `${AppConfig.constants.links.pointsGeoJsonService}?fileName=${archive}`,
            {
                credentials: 'include',
            },
        );
        if (!signedUrlResponse.ok) {
            throw new Error(`Points signed URL request failed: ${signedUrlResponse.status}`);
        }
        const { url } = await signedUrlResponse.json();
        if (!url) throw new Error('Points signed URL missing');

        const [cachedContent, cachedETag] = await Promise.all([
            dbGet(collectionName, collectionKey),
            dbGet(collectionName, eTagCollectionKeyName),
        ]);
        const hasFullCache = cachedContent != null && typeof cachedETag === 'string';

        const response = await fetch(
            `${AppConfig.constants.links.proxyService}?url=${encodeURIComponent(url)}`,
            {
                mode: 'cors',
                credentials: 'include',
                headers: hasFullCache ? { 'If-None-Match': cachedETag } : undefined,
            },
        );

        let zipBlob;
        if (response.status === 200) {
            zipBlob = await response.blob();
            await dbSet(collectionName, collectionKey, zipBlob);
            await dbSet(collectionName, eTagCollectionKeyName, response.headers.get('etag'));
        } else if (response.status === 304) {
            zipBlob = cachedContent;
        } else {
            throw new Error(`Points data request failed: ${response.status}`);
        }

        let geoJson;
        try {
            const zip = await new JSZip().loadAsync(zipBlob);
            geoJson = JSON.parse(await zip.file(raw).async('string'));
        } catch (e) {
            geoJson = undefined;
        }
        if (!geoJson || !geoJson.features) {
            await dbSet(collectionName, eTagCollectionKeyName, null);
            throw new Error('Points data is unreadable');
        }
        return geoJson;
    };

    loadPointsMetadata = () => {
        if (!this._pointsMetadataLoad) {
            this._pointsMetadataLoad = retry(this.fetchPointsMetadata, POINTS_LOAD_RETRY).then(
                metadata => {
                    this._pointsMetadata = metadata;
                },
                e => console.error(e),
            );
        }
        return this._pointsMetadataLoad;
    };

    /** @returns {Promise<import('../../../').PointsMetadata>} */
    fetchPointsMetadata = async () => {
        const response = await fetch(
            `${AppConfig.constants.baseURL}/json/metadata/${AppConfig.constants.customPointMetadataJsonFileName}`,
        );
        if (!response.ok) throw new Error(`Points metadata request failed: ${response.status}`);
        const metadata = await response.json();
        if (!metadata.filterProperties || !metadata.filterProperties.length) {
            throw new Error('Missing filters in points metadata');
        }
        if (!metadata.searchProperties || !metadata.searchProperties.length) {
            throw new Error('Missing search properties in points metadata');
        }
        return metadata;
    };

    /**
     * @param {import('../types').OlapSiteAnalysisPayload} payload
     * @param {'selfstorage-facility' | 'selfstorage-facility-with-pricing' | 'demography' | 'executive-summary' | 'executive-summary-detailed' | 'housing-start'} q
     * @param {number} selection
     * @returns {Promise<any>}
     */
    fetchOlapSiteAnalysisForSelection(payload, q, selection) {
        return new Promise((resolve, reject) => {
            const query = {
                lat: payload.site.lat,
                lng: payload.site.lng,
                q,
                profile: payload.reportContours.type,
                'profile-value': selection,
            };
            if (payload.reportContours.polygon) query.polygon = payload.reportContours.polygon;

            Api.olap.siteAnalysis({ query }).then(response => {
                if (response.status === 200) resolve(response.body);
                else reject(JSON.stringify(response));
            }, reject);
        });
    }
}

export default PointsDataSource;
